import React from 'react';
import PropTypes from 'prop-types';
import { ToastContext, Modal, Button, CheckboxLabel, TooltipHelp, SelectHierarchy, Message } from '@weezevent/nacre';
import * as Sentry from '@sentry/browser';

import { useTranslation } from 'react-i18next';
import { useForm } from '../../../utils/validators';
import Style from './swapSessionPostAction.module.css';
import SessionModel from '../../../models/sessionModel';
import useAppContext from '../../../context/useAppContext';
import i18n from 'i18next';
import LoadingCard from '../../../components/loadingCard';

function SelectHierarchySessions({ sessions, sessionsTree, form, onChangeForm }) {
    const { t } = useTranslation();

    return !sessions.length ? (
        <Message type="warning" title={t('ticket.common.warning')}>
            {t('ticket.manager.attendees.action.swap_session.noSessionsAvailable')}
        </Message>
    ) : (
        <div className={Style['select-sessions__container']}>
            <SelectHierarchy
                options={sessionsTree}
                searchable
                placeholder={t('ticket.manager.attendees.action.swap_session.placeholderSelect')}
                placeholderSearch={t('ticket.common.action.search')}
                chevron
                widthIndent={'15px'}
                onChange={(optionValue, option) => onChangeForm('session_uuid', optionValue)}
                value={form.session_uuid}
            />
        </div>
    );
}

export default function SwapSessionPostAction({ actionInstance = null, onClose = () => {}, sync = () => {} }) {
    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const toast = React.useContext(ToastContext);
    const [{ event, organization, event_timezone }] = useAppContext();
    const locale = i18n.language;

    const [sessions, loadingSessions] = SessionModel.useApiModel(
        {
            organizationId: organization.pk,
            eventId: event.pk,
            query: {
                limit: 9999,
                rates__in: [actionInstance.filtered_items[0]?.rate.id]
            }
        },
        [organization, event, actionInstance.filtered_items]
    );

    const sessionsTree = React.useMemo(() => {
        return SessionModel.toOptions({
            sessions,
            timezone: event_timezone,
            locale,
            remainingQuota: sessionOption => {
                return SessionModel.sessionRemainingQuota({ session: sessionOption, rate: actionInstance.filtered_items[0]?.rate });
            },
            extraSession: sessionOption => <span>{sessionOption.remaining_quota === null ? '-' : sessionOption.remaining_quota}</span>
        });
    }, [sessions, event, event_timezone, locale, actionInstance.filtered_items]);

    const [form, setForm] = useForm({
        session_uuid: null,
        send_mail: false,
        rotate_barcode: false
    });

    const data = React.useMemo(() => {
        let _data = {
            session_uuid: form.session_uuid,
            send_mail: form.send_mail,
            rotate_barcode: form.rotate_barcode,
            id: actionInstance.filtered_items[0].id,
            mode: actionInstance.constructor.TYPE
        };
        if (actionInstance?.filtered_orders) {
            _data.items = actionInstance.filtered_orders;
            _data.mode = 'orders';
        }
        if (actionInstance?.filtered_attendees) {
            _data.items = actionInstance.filtered_attendees;
            _data.mode = 'attendees';
        }
        return _data;
    }, [form, actionInstance]);

    const handleSubmit = React.useCallback(() => {
        setIsSubmitting(true);
        actionInstance
            .runPostAction(data)
            .then(response => {
                toast.success(t(`ticket.manager.${data.mode}.action.` + actionInstance.constructor.NAME + '.toast.success'));
                setIsSubmitting(false);
                onClose();
                sync();
            })
            .catch(e => {
                console.error(e);
                Sentry.captureException(e);
                toast.error(t(`ticket.manager.${data.mode}.action.` + actionInstance.constructor.NAME + '.toast.error'));
                setIsSubmitting(false);
            });
    }, [actionInstance, onClose, data]);

    const _onClose = React.useCallback(() => {
        onClose();
        setIsSubmitting(false);
    }, [onClose]);

    return (
        <Modal
            size="medium"
            open={Boolean(actionInstance)}
            onClose={_onClose}
            title={actionInstance ? t('ticket.manager.' + data.mode + '.action.' + actionInstance.constructor.NAME + '.title') : ''}
            subtitle={actionInstance ? t('ticket.manager.' + data.mode + '.action.' + actionInstance.constructor.NAME + '.subtitle') : ''}
            submitButton={
                <Button primary onClick={handleSubmit} disabled={isSubmitting || !form.session_uuid} isLoading={isSubmitting} label={t('ticket.common.action.confirm')} />
            }
            cancelButton={<Button onClick={_onClose} label={t(`ticket.common.action.cancel`)} />}
        >
            {actionInstance.currentSessionDate && (
                <div>
                    <div className={Style['current-session__label']}>{t('ticket.manager.attendees.action.swap_session.currentSessionLabel')}</div>
                    <div className={Style['current-session__date']}>{actionInstance.currentSessionDate}</div>
                </div>
            )}
            <LoadingCard loading={loadingSessions} height={'20vh'}>
                <SelectHierarchySessions sessions={sessions} sessionsTree={sessionsTree} form={form} onChangeForm={setForm} />
                <div>
                    <div className={Style['infoText']}>{t('ticket.manager.attendees.action.' + actionInstance.constructor.NAME + '.infoText')}</div>
                    <div className={Style['checkbox-and-tooltip-send-mail']}>
                        <CheckboxLabel
                            label={t('ticket.manager.attendees.action.' + actionInstance.constructor.NAME + '.sendOrderLabel')}
                            onChange={(e, { checked }) => setForm('send_mail', checked)}
                            checked={form.send_mail}
                        />
                        <TooltipHelp triggerOnHover position="bottom" title={t('ticket.manager.attendees.action.' + actionInstance.constructor.NAME + '.sendOrderTooltip')} />
                    </div>
                    <div className={Style['checkbox-and-tooltip']}>
                        <CheckboxLabel
                            label={t('ticket.manager.attendees.action.' + actionInstance.constructor.NAME + '.rotateBarcodeLabel')}
                            onChange={(e, { checked }) => setForm('rotate_barcode', checked)}
                            checked={form.rotate_barcode}
                        />
                        <TooltipHelp triggerOnHover position="bottom" title={t('ticket.manager.attendees.action.' + actionInstance.constructor.NAME + '.rotateBarcodeTooltip')} />
                    </div>
                </div>
            </LoadingCard>
        </Modal>
    );
}

SwapSessionPostAction.propTypes = {
    actionInstance: PropTypes.object,
    onClose: PropTypes.func,
    sync: PropTypes.func
};
